import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  PlayCircle,
  SpeakerHigh,
  Image as ImageIcon,
  ArrowUpRight,
  Sparkle,
  PencilSimple,
} from "@phosphor-icons/react";
import { getPublicShare } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_KINDS = ["image", "video", "audio", "imagined", "memory", "sketch", "web"];

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  // When the share link ends with `#ref=<refId>` we scroll to that card and
  // give it a brief ring so the recipient knows exactly which image was shared.
  const [highlightRefId, setHighlightRefId] = useState(() => {
    try {
      const h = window.location.hash || "";
      const m = /#ref=([a-f0-9-]+)/i.exec(h);
      return m ? m[1] : "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    let alive = true;
    getPublicShare(token)
      .then((d) => alive && setData(d))
      .catch((e) =>
        alive && setError(
          e?.response?.status === 404
            ? "This board doesn't exist or isn't shared anymore."
            : "Couldn't load this board. Try again in a moment.",
        ),
      );
    return () => {
      alive = false;
    };
  }, [token]);

  // Once cards render, scroll the highlighted reference into view.
  useEffect(() => {
    if (!highlightRefId || !data) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="share-ref-${highlightRefId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber", "ring-offset-2", "ring-offset-bg");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-amber", "ring-offset-2", "ring-offset-bg");
          setHighlightRefId("");
        }, 3200);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [data, highlightRefId]);

  const refs = data?.references || [];
  const cards = useMemo(() => {
    const hasVisual = (r) => {
      if (brokenIds.has(r.id)) return false;
      if (r.asset_id) return true;
      if (r.image_url) return true;
      if (r.kind === "audio") return true;
      if (r.kind === "web") return true;
      return false;
    };
    const eligible = refs.filter(
      (r) => CARD_KINDS.includes(r.kind) && hasVisual(r),
    );
    const buckets = {
      sketch: [], imagined: [], image: [], video: [], audio: [], memory: [], web: [],
    };
    const starred = [];
    for (const r of eligible) {
      if (r.starred) starred.push(r);
      else if (buckets[r.kind]) buckets[r.kind].push(r);
    }
    buckets.image.sort((a, b) => (b.score || 0) - (a.score || 0));
    const picks = [...starred.slice(0, 10)];
    const draw = (kind, n) => {
      for (let i = 0; i < n && buckets[kind].length && picks.length < 10; i++) {
        picks.push(buckets[kind].shift());
      }
    };
    draw("sketch", 1);
    draw("imagined", 2);
    draw("image", 3);
    draw("video", 2);
    draw("audio", 1);
    draw("memory", 1);
    for (const k of ["imagined", "image", "video", "audio", "memory", "web"]) {
      draw(k, 10);
      if (picks.length >= 10) break;
    }
    return picks.slice(0, 10);
  }, [refs, brokenIds]);

  const handleBroken = (id) => {
    setBrokenIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-bg flex flex-col">
        <ShareTopBar />
        <main className="flex-1 max-w-[720px] mx-auto px-6 pt-24 text-center">
          <p className="text-[13px] text-ink-dim" data-testid="share-error">{error}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 bg-amber hover:bg-amber-hover text-[#20170a] rounded-full px-5 py-3 text-[14px] font-semibold"
          >
            Build your own board on Recce →
          </Link>
        </main>
        <ShareFoot />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-bg">
        <ShareTopBar />
        <main className="max-w-[920px] mx-auto px-6 pt-24 text-center text-ink-dim text-sm">
          loading board…
        </main>
      </div>
    );
  }

  const { recce } = data;

  return (
    <div className="min-h-screen bg-bg" data-testid="share-page">
      <ShareTopBar />

      <main className="max-w-[920px] mx-auto px-6 pb-20">
        <section className="pt-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber mb-3">
            A Recce board · shared by {recce.owner_name}
          </p>
          <h1
            className="font-serif font-medium leading-[1.15] max-w-[36ch]"
            style={{ fontSize: "clamp(26px, 3.5vw, 40px)" }}
            data-testid="share-title"
          >
            “{recce.title || recce.brief}”
          </h1>
          <p className="text-[13px] text-ink-dim mt-3">
            {cards.length} reference{cards.length === 1 ? "" : "s"} ·{" "}
            Scouted by Recce
            {recce.ai_generated && " · includes AI-generated images"}
          </p>
        </section>

        {cards.length === 0 ? (
          <div className="mt-14 border border-dashed border-line rounded-xl p-10 text-center text-[13px] text-ink-dim">
            This board doesn't have any references yet.
          </div>
        ) : (
          <div
            className="mt-8"
            style={{ columnCount: 3, columnGap: 14 }}
            data-testid="share-grid"
          >
            <style>{`
              @media (max-width: 760px) { [data-testid="share-grid"] { column-count: 2 !important; } }
              @media (max-width: 480px) { [data-testid="share-grid"] { column-count: 1 !important; } }
            `}</style>
            {cards.map((r, i) => (
              <ShareCard key={r.id} ref_={r} index={i} onBroken={handleBroken} />
            ))}
          </div>
        )}

        {/* Growth CTA — the whole point of sharing. */}
        <section
          className="mt-16 border-t border-line pt-10 text-center"
          data-testid="share-cta"
        >
          <p className="font-serif text-[22px] leading-snug max-w-[36ch] mx-auto">
            Want Recce digging for references on <em className="not-italic text-amber">your</em> idea?
          </p>
          <p className="text-[14px] text-ink-dim mt-3 max-w-[48ch] mx-auto">
            Drop in a raw idea. Recce explores the open web and brings back a moodboard — while you sleep.
          </p>
          <Link
            to="/"
            data-testid="share-cta-btn"
            className="mt-6 inline-flex items-center gap-2 bg-amber hover:bg-amber-hover text-[#20170a] rounded-full px-5 py-3 text-[14px] font-semibold"
          >
            Try Recce — free
          </Link>
        </section>
      </main>

      <ShareFoot />
    </div>
  );
}

const ShareTopBar = () => (
  <header className="flex items-center justify-between gap-4 px-6 pt-7 max-w-[920px] mx-auto">
    <Link
      to="/"
      className="flex items-baseline gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      data-testid="share-brand"
      aria-label="Recce home"
    >
      <span className="font-serif text-[21px] font-semibold tracking-[0.2px] text-amber">
        Recce
      </span>
    </Link>
    <Link
      to="/"
      className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-dim hover:text-amber"
      data-testid="share-home-link"
    >
      what is recce →
    </Link>
  </header>
);

const ShareFoot = () => (
  <footer className="border-t border-line mt-10 px-6 py-6 text-center text-[11px] text-ink-ghost">
    Boards are read-only. The owner can revoke the link anytime.
  </footer>
);

const refImageSrc = (r) => {
  if (r.asset_id) return `${API}/assets/${r.asset_id}`;
  return r.image_url || "";
};

const kindLabel = (r) => {
  if (r.kind === "image") return "Image";
  if (r.kind === "video") return "Video clip";
  if (r.kind === "audio") return "Mood track";
  if (r.kind === "memory") return "From memory";
  if (r.kind === "imagined") return "AI-generated";
  if (r.kind === "sketch") return "Sketch";
  return "Article";
};

const KindIcon = ({ kind }) => {
  const c = { size: 12, weight: "regular" };
  if (kind === "video") return <PlayCircle {...c} />;
  if (kind === "audio") return <SpeakerHigh {...c} />;
  if (kind === "imagined") return <Sparkle {...c} weight="fill" />;
  if (kind === "sketch") return <PencilSimple {...c} />;
  if (kind === "image" || kind === "memory") return <ImageIcon {...c} />;
  return null;
};

const ShareCard = ({ ref_, index, onBroken }) => {
  const img = refImageSrc(ref_);
  const isTextOnly = ref_.kind === "web" && !img;
  const isImagined = ref_.kind === "imagined";
  const isSketch = ref_.kind === "sketch";
  const hasImage = Boolean(img) && !isTextOnly;

  return (
    <article
      className={`relative break-inside-avoid mb-[14px] rounded-[14px] overflow-hidden bg-surface border opacity-0 translate-y-2.5 animate-card-in ${
        isSketch ? "border-ink shadow-[4px_4px_0_0_rgba(181,101,29,0.85)]" : "border-line"
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`share-ref-${ref_.id}`}
    >
      {isImagined && (
        <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-bg/95 border border-line text-[9.5px] uppercase tracking-[0.18em] text-amber rounded-full px-2 py-1">
          <Sparkle size={9} weight="fill" /> AI-generated
        </span>
      )}
      {img && !isTextOnly ? (
        <div className="relative">
          <img
            src={img}
            alt={ref_.title}
            loading="lazy"
            className="w-full block"
            style={{ maxHeight: 320, objectFit: "cover" }}
            onError={() => hasImage && !ref_.asset_id && onBroken?.(ref_.id)}
          />
          {ref_.kind === "video" && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="bg-ink/80 rounded-full p-3">
                <PlayCircle size={22} weight="fill" className="text-bg" />
              </span>
            </span>
          )}
        </div>
      ) : ref_.kind === "audio" ? (
        <div className="w-full h-[150px] bg-surface-hi flex items-center justify-center text-amber/70">
          <SpeakerHigh size={32} weight="thin" />
        </div>
      ) : (
        <div className="w-full px-[13px] pt-4 pb-3 bg-surface-hi border-b border-line">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-amber truncate">
              {hostname(ref_.url) || ref_.source || "article"}
            </span>
            <ArrowUpRight size={11} className="text-amber shrink-0" />
          </div>
          <p className="font-serif text-[15px] leading-[1.25] text-ink line-clamp-4">
            “{ref_.title || ref_.snippet || "Untitled"}”
          </p>
        </div>
      )}

      <div className="px-[13px] pt-3 pb-[14px]">
        <p className="text-[11px] font-semibold text-amber mb-1 flex items-center gap-1.5">
          <KindIcon kind={ref_.kind} />
          {kindLabel(ref_)}
        </p>
        {!(ref_.kind === "web" && !img) && (
          <p className="text-[13px] text-ink leading-[1.4] mb-1 line-clamp-2">
            {ref_.title || ref_.snippet || "Untitled"}
          </p>
        )}
        {ref_.url && (
          <a
            href={ref_.url}
            target="_blank"
            rel="noreferrer"
            className="text-[11.5px] text-ink-ghost hover:text-amber inline-flex items-center gap-1"
            data-testid={`share-src-${ref_.id}`}
          >
            {ref_.source || hostname(ref_.url) || "open source"}
            <ArrowUpRight size={10} />
          </a>
        )}
      </div>
    </article>
  );
};

function hostname(u) {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}
